import { useState } from 'react'
import { cx } from '../../components/ui/cx'
import { csvImportRejection } from '../../lib/import-file-type'
import { parseCartCsv } from './cart-csv'
import type { CartCsvRow } from './cart-csv'
import { CART_CSV_MAX_FILE_SIZE_BYTES } from './cart-limits'

/** File picker filter: plain-text tabular formats only. Excel/spreadsheet
 * workbooks are deliberately NOT listed — a customer can still force one
 * through "All files", and `handleFile` then rejects it with a clear message
 * (shared with the CAD/BOM upload widget) rather than reading its bytes as
 * text. */
const CSV_ACCEPT_ATTR = '.csv,.tsv,.tab,.txt,text/csv,text/tab-separated-values,text/plain'

export interface CsvImportControlProps {
  readonly disabled?: boolean
  /** Called with the parsed rows + any per-row parse errors once a file is
   * selected. Resolving SKUs against the catalog and POSTing lines is the
   * caller's job (CartPage) — this control only owns file-read + CSV parse. */
  readonly onParsed: (result: { rows: readonly CartCsvRow[]; errors: readonly string[] }) => void
  /** Extra class(es) on the root — lets a caller restyle the trigger (e.g.
   * the Cart header's compact button row) without forking this component. */
  readonly className?: string
  /** Renders the "big tile" treatment used by the Cart empty state's "Upload
   * a CSV" option, instead of the compact header-button treatment. Same
   * underlying `<input type="file">` either way. */
  readonly tile?: boolean
}

/**
 * The Cart screen's CSV bulk-import control (screens.md Cart §12). A plain
 * file input (native, so keyboard/screen-reader support is free) accepting
 * a `sku,quantity`-per-line CSV; parsing itself is delegated to the pure
 * `parseCartCsv` (cart-csv.ts) so this component stays a thin file-read
 * wrapper around it.
 */
export function CsvImportControl({
  disabled = false,
  onParsed,
  className,
  tile = false,
}: CsvImportControlProps) {
  const [readError, setReadError] = useState<string>()

  function handleFile(file: File) {
    setReadError(undefined)

    // Reject an unsupported file type BEFORE reading — an `.xlsx` (or any
    // binary) read as text yields a wall of nonsense "rows". This uses the
    // SAME shared gate + wording as the CAD/BOM upload widget so the same file
    // is rejected the same way in both places (Excel → "save as CSV / paste").
    const typeRejection = csvImportRejection(file)
    if (typeRejection) {
      setReadError(typeRejection)
      return
    }

    // Security review (MED/LOW): reject an oversized file before it's ever
    // read into memory — an unbounded `FileReader.readAsText` on a huge
    // file can pin the tab, and `parseCartCsv`'s own row cap can't help
    // here because it only runs after the whole file is already in
    // memory as a string.
    if (file.size > CART_CSV_MAX_FILE_SIZE_BYTES) {
      const maxKb = Math.round(CART_CSV_MAX_FILE_SIZE_BYTES / 1000)
      setReadError(
        `That file is too large (max ${maxKb}KB). Split it into smaller files and try again.`,
      )
      return
    }

    // `FileReader`, not `File.prototype.text()` — jsdom (the test
    // environment) doesn't implement the latter, and FileReader has been
    // universally supported for far longer besides.
    const reader = new FileReader()
    reader.onload = () => {
      onParsed(parseCartCsv(typeof reader.result === 'string' ? reader.result : ''))
    }
    reader.onerror = () => {
      setReadError('Could not read that file. Please try again.')
    }
    reader.readAsText(file)
  }

  // The file <input> is inlined literally inside each <label> below (rather
  // than shared via a variable) so it stays NESTED-ONLY. A label that both
  // wraps AND points at the same input via htmlFor fires the file dialog twice
  // and the browser cancels the duplicate, so the picker never opens (the
  // UID-60 "Upload a CSV does nothing" bug). Nesting alone = one clean trigger.
  if (tile) {
    // The "big tile" treatment (Cart empty state's "Upload a CSV" option):
    // the whole card is the `<label>`, same click-anywhere-activates-the-
    // input behavior as `CopyFromExcelModal`'s trigger sibling tile.
    return (
      <div className={cx('cart-csv-import', className)}>
        {/* The <input> is nested inside this <label> (implicit association),
            so clicking anywhere on the tile opens the file picker. Do NOT also
            add htmlFor: a label that both wraps AND points at the same input
            fires the file dialog twice, and browsers cancel the duplicate —
            the picker never appears (UID-60 bug). */}
        <label className="cart-bulk-tile">
          <input
            type="file"
            accept={CSV_ACCEPT_ATTR}
            disabled={disabled}
            onChange={(event) => {
              const file = event.target.files?.[0]
              event.target.value = ''
              if (file) {
                handleFile(file)
              }
            }}
          />
          <span className="cart-bulk-tile-icon" aria-hidden="true">
            <UploadIcon />
          </span>
          <span className="cart-bulk-tile-body">
            <span className="cart-bulk-tile-title">Upload a CSV</span>
            <span className="cart-bulk-tile-detail">
              Import a spreadsheet of part numbers and quantities.
            </span>
          </span>
          <span className="cart-bulk-tile-chevron" aria-hidden="true">
            <ChevronRightIcon />
          </span>
        </label>
        {readError && (
          <p role="alert" className="form-error-text">
            {readError}
          </p>
        )}
      </div>
    )
  }

  return (
    <div className={cx('cart-csv-import', className)}>
      {/* Implicit label→input nesting only; no htmlFor (see the tile note). */}
      <label className="cart-csv-import-trigger">
        <input
          type="file"
          accept={CSV_ACCEPT_ATTR}
          disabled={disabled}
          onChange={(event) => {
            const file = event.target.files?.[0]
            event.target.value = ''
            if (file) {
              handleFile(file)
            }
          }}
        />
        <UploadIcon />
        Upload CSV
      </label>
      <p className="caption-text sr-only">
        One row per line: {'sku,quantity'} (an optional header row is fine).
      </p>
      {readError && (
        <p role="alert" className="form-error-text">
          {readError}
        </p>
      )}
    </div>
  )
}

/** Lucide "upload" glyph (tray + up arrow) — the design's upload icon, in
 * place of the Ant filled `UploadOutlined`. */
function UploadIcon() {
  return (
    <svg
      aria-hidden="true"
      width="19"
      height="19"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" x2="12" y1="3" y2="15" />
    </svg>
  )
}

/** Lucide chevron-right for the bulk tile. */
function ChevronRightIcon() {
  return (
    <svg
      aria-hidden="true"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="m9 18 6-6-6-6" />
    </svg>
  )
}
